import React from 'react'
import useDimensions from 'hooks/useDimensions'
import { CategoryVizMarker } from 'components/CategoryVizMarker'
import styled from 'styled-components'
import { CATEGORY_VIZ_HEIGHT } from '../globals'
import { scaleLinear } from 'd3-scale'
import { group } from 'd3-array'

const Container = styled.div`
  width: 100%;
  line-height: 0;
`

const SVG = styled.svg`
  width: 100%;
  height: ${CATEGORY_VIZ_HEIGHT}px;
`

const MainAxis = styled.polygon`
  fill: #202346;
  stroke: #202346;
`

const IndikatorLabel = styled.div`
  line-height: 1.4em;
  font-family: var(--chart-font-stack);
  font-size: 18px;
  width: 100%;
  text-align: center;
`
const UnitLabel = styled.div`
  line-height: 1.4em;
  color: #7b7768;
  font-family: var(--chart-font-stack);
  font-size: 15px;
  width: 100%;
  text-align: center;
`

export const CategoryViz = ({
  name,
  unit,
  dir,
  data,
  isMobile = false,
  tenant
}: {
  name: string
  unit: string
  dir: directionType
  data: {
    label: string
    value: dataElement
    code: string
  }[]
  isMobile: boolean
  tenant: string
}) => {
  const [REF, size, node] = useDimensions()
  const y = CATEGORY_VIZ_HEIGHT / 2
  const padding = isMobile ? 15 : 35

  const scales = {
    normal: scaleLinear()
      .domain([0, 100])
      .range([padding, size.width - padding]),
    reverse: scaleLinear()
      .domain([100, 0])
      .range([padding, size.width - padding])
  }
  const sorts = {
    normal: (a, b) => a[1][0].value.norm - b[1][0].value.norm,
    reverse: (a, b) => b[1][0].value.norm - a[1][0].value.norm
  }

  return (
    <Container>
      <IndikatorLabel>{name}</IndikatorLabel>
      <UnitLabel>{unit}</UnitLabel>
      <SVG ref={REF}>
        {size && (
          <>
            <MainAxis
              points={(dir === 'reverse'
                ? [
                    [padding, y],
                    [padding, y + 7],
                    [size.width - padding, y + 2],
                    [size.width - padding, y]
                  ]
                : [
                    [padding, y],
                    [padding, y + 2],
                    [size.width - padding, y + 7],
                    [size.width - padding, y]
                  ]
              ).join(' ')}
            />
          </>
        )}

        {data &&
          Array.from(group(data, d => d.value.abs))
            .filter(f => f[0] || f[0] === 0)
            .sort(sorts[dir])
            .map(m => {
              const val: number = m[1][0].value.abs
              const codes = m[1].map(m => m.code)
              const label = m[1].length === 1 ? m[1][0].label : codes
              const code = m[1].length === 1 ? m[1][0].code : ''
              const norm = m[1][0].value.norm || 0
              const x = scales[dir](norm)
              const color = m[1][0].value.color || '#000'
              return (
                <CategoryVizMarker
                  x={x}
                  y={y}
                  color={color}
                  label={label}
                  code={code}
                  labelAnchor={
                    x === padding
                      ? 'start'
                      : x === size.width - padding
                      ? 'end'
                      : 'middle'
                  }
                  value={val}
                  alwaysLabeled={norm === 0 || norm === 100}
                  isMarked={
                    (code === tenant || codes.indexOf(tenant) !== -1) &&
                    tenant !== ''
                  }
                />
              )
            })}
      </SVG>
    </Container>
  )
}
